import { produce } from 'immer';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  LoadingObject,
  LoadingState,
} from '../../../../../common/global/models';
import useMount from '../../../../../common/hooks/useMount';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { rewriteImageUrl } from '../../../helpers';
import {
  Category,
  CategoryDetails,
  Dictionary,
} from '../../../models/Categories';
import { resources } from '../../../resources/resources';
import { categoryService } from '../../../services/category';
import { MyBenefitsNavigationState } from '../types';
import FixedBenefitDialog from './BenefitDetailsDialog';

export default function FixedBenefits() {
  const { t: translate } = useTranslation();
  const location = useLocation();
  const navigationState = location.state as MyBenefitsNavigationState | null;
  const [activeBenefitId, setActiveBenefitId] = useState<number | null>();
  const [categoriesDetails, setCategoriesDetails] = useState<
    Dictionary<LoadingObject<CategoryDetails>>
  >({});
  const [rootCategory, setRootCategory] = useState<Category>();
  const [statusText, setStatusText] = useState<string>('');
  const activeBenefit = _.find(
    rootCategory?.children,
    c => c.id == activeBenefitId,
  );

  const loadBenefitDetails = async (benefitId: number) => {
    setActiveBenefitId(benefitId);

    setCategoriesDetails(
      produce(draft => {
        draft[benefitId] = { state: LoadingState.Requested };
      }),
    );

    const result = await categoryService.getCategoryDetails(benefitId);

    if (!result.hasErrors) {
      setCategoriesDetails(
        produce(draft => {
          draft[benefitId] = {
            value: { ...result.data },
            state: LoadingState.Ready,
          };
        }),
      );
    }
  };

  const handleBenefitClick = async (benefitId: number) => {
    await loadBenefitDetails(benefitId);
  };

  const handleBenefitMouseOver = (category: Category) => {
    setStatusText(`${category.name} (${category.id})`);
  };

  const handleBenefitMouseOut = () => {
    setStatusText('');
  };

  useMount(abortController => {
    (async () => {
      const result = await categoryService.getFixedBenefits({
        signal: abortController?.signal,
      });

      if (!result.hasErrors) {
        setRootCategory(result.data);
      }
    })();
  });

  useEffect(() => {
    if (!navigationState?.activeBenefitId || !rootCategory?.children) {
      return;
    }

    const benefitExists = rootCategory.children.some(
      benefit => benefit.id === navigationState.activeBenefitId,
    );

    if (!benefitExists) {
      return;
    }

    loadBenefitDetails(navigationState.activeBenefitId);
  }, [navigationState?.activeBenefitId, rootCategory?.children]);

  return (
    <section className="page-section mb-60">
      <p className="mb-25">
        {translate(resources.myBenefitsPage.fixedBenefitsCopy)}
      </p>

      <Loader loading={!rootCategory} loader={<LoadingSpinner />}>
        <div className="card-list card-list-6 card-list-md-5 card-list-sm-4 card-list-xs-3 card-list-xxs-2">
          {_.map(rootCategory?.children, category => {
            const imgSrc = rewriteImageUrl(category.image2 || category.image);
            return (
              <a
                className="card category-card"
                key={category.id}
                onClick={() => handleBenefitClick(category.id)}
                onMouseOver={() => handleBenefitMouseOver(category)}
                onMouseOut={handleBenefitMouseOut}
              >
                <div className="card-img">
                  <img src={imgSrc} alt={category.name} />
                </div>
                <div className="card-details">
                  <div className="card-title">{category.name}</div>
                </div>
              </a>
            );
          })}
        </div>
      </Loader>

      {!_.isNil(activeBenefit) && (
        <FixedBenefitDialog
          onClose={() => setActiveBenefitId(null)}
          benefitDetails={categoriesDetails[activeBenefitId!]}
          benefit={activeBenefit}
        />
      )}
      <div
        style={{
          position: 'fixed',
          bottom: '0',
          left: '0',
          marginBottom: '1px',
          marginLeft: '5px',
          background: '#d5e4e6',
          color: '#9ea3a3',
          fontSize: 14,
        }}
      >
        {statusText}
      </div>
    </section>
  );
}
